<template>
  <div class="app-container kb-page">
    <!-- 筛选与操作栏 -->
    <el-card shadow="never" class="kb-toolbar-card">
      <div class="kb-toolbar">
        <el-select
          v-model="filterCategory"
          class="kb-filter-category"
          placeholder="所有分类"
          clearable
          @change="onFilterCategoryChange"
        >
          <el-option v-for="c in categories" :key="c.id" :label="c.name" :value="c.id" />
        </el-select>
        <el-input
          v-model="keyword"
          class="kb-filter-keyword"
          placeholder="搜索标题/关键字"
          clearable
          @keyup.enter.native="searchArticles"
          @clear="searchArticles"
        >
          <i slot="suffix" class="el-input__icon el-icon-search" @click="searchArticles"></i>
        </el-input>
        <el-button icon="el-icon-setting" @click="openCategoryDrawer">管理分类</el-button>
        <el-tag type="primary">{{ total }} 篇文章</el-tag>
        <el-popconfirm
          v-if="selectedRowKeys.length > 0"
          :title="`确定删除选中的 ${selectedRowKeys.length} 篇文章？`"
          @confirm="handleBatchDelete"
        >
          <el-button slot="reference" size="small" type="danger" icon="el-icon-delete">
            批量删除 ({{ selectedRowKeys.length }})
          </el-button>
        </el-popconfirm>
        <div class="kb-toolbar-spacer" />
        <el-button icon="el-icon-upload2" @click="openImportModal">批量导入</el-button>
        <el-button icon="el-icon-download" :loading="exporting" @click="handleExport">导出全部</el-button>
        <el-button type="primary" icon="el-icon-plus" @click="openArticleModal()">新增文章</el-button>
      </div>
    </el-card>

    <!-- 文章列表 -->
    <el-table
      :data="articles"
      v-loading="loading"
      row-key="id"
      @selection-change="onSelectionChange"
    >
      <el-table-column type="selection" width="40" />
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
      <el-table-column label="分类" width="110">
        <template slot-scope="{ row }">
          <el-tag>{{ row.categoryName }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="menuCode" label="菜单代码" width="100" />
      <el-table-column prop="keywords" label="关键字" width="150" show-overflow-tooltip />
      <el-table-column label="状态" width="80">
        <template slot-scope="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'info'">{{ row.status === 1 ? '启用' : '停用' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="190">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="openDetailDrawer(row)">查看</el-button>
          <el-button type="text" size="small" @click="openArticleModal(row)">编辑</el-button>
          <el-popconfirm title="确定删除？" @confirm="removeArticle(row.id)">
            <el-button slot="reference" type="text" size="small" class="kb-danger">删除</el-button>
          </el-popconfirm>
        </template>
      </el-table-column>
      <div slot="empty">暂无文章</div>
    </el-table>
    <el-pagination
      class="kb-pagination"
      layout="total, prev, pager, next"
      :current-page="page"
      :page-size="10"
      :total="total"
      @current-change="onPageChange"
    />

    <!-- 文章编辑弹窗 -->
    <el-dialog :visible.sync="modalOpen" :title="editingArticle ? '编辑文章' : '新增文章'" width="640px">
      <el-form ref="articleFormRef" :model="articleForm" :rules="articleRules" label-position="top">
        <el-form-item label="分类" prop="categoryId">
          <el-select v-model="articleForm.categoryId" class="full-width" placeholder="请选择分类">
            <el-option v-for="c in categories" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="菜单代码" prop="menuCode">
          <el-input v-model="articleForm.menuCode" />
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input v-model="articleForm.title" />
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <el-input v-model="articleForm.content" type="textarea" :rows="6" />
        </el-form-item>
        <el-form-item label="关键字" prop="keywords">
          <el-input v-model="articleForm.keywords" placeholder="多个关键字用逗号分隔" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="articleForm.status" class="full-width">
            <el-option label="启用" :value="1" />
            <el-option label="停用" :value="0" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="modalOpen = false">取消</el-button>
        <el-button type="primary" @click="submitArticle">确定</el-button>
      </div>
    </el-dialog>

    <!-- 文章详情弹窗 -->
    <el-dialog :visible.sync="detailDrawerOpen" title="文章详情" width="500px">
      <el-form v-if="detailArticle" label-width="80px">
        <el-form-item label="ID">{{ detailArticle.id }}</el-form-item>
        <el-form-item label="标题">{{ detailArticle.title }}</el-form-item>
        <el-form-item label="分类">
          <el-tag>{{ detailArticle.categoryName }}</el-tag>
        </el-form-item>
        <el-form-item label="菜单代码">{{ detailArticle.menuCode }}</el-form-item>
        <el-form-item label="关键字">
          <el-tag v-for="k in keywordTags(detailArticle.keywords)" :key="k" style="margin-right: 4px">{{ k }}</el-tag>
        </el-form-item>
        <el-form-item label="状态">
          <el-tag :type="detailArticle.status === 1 ? 'success' : 'info'">
            {{ detailArticle.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </el-form-item>
        <el-form-item label="内容">
          <div class="kb-detail-content">{{ detailArticle.content }}</div>
        </el-form-item>
      </el-form>
    </el-dialog>

    <!-- 分类管理弹窗 -->
    <el-dialog :visible.sync="categoryDrawerOpen" title="知识库分类管理" width="500px">
      <div class="kb-category-toolbar">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openCategoryModal()">新增分类</el-button>
      </div>
      <el-table :data="categoryRows" v-loading="categoryLoading" row-key="id" size="small">
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column prop="name" label="名称" min-width="120" />
        <el-table-column prop="sortOrder" label="排序" width="80" />
        <el-table-column label="状态" width="80">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'">{{ row.status === 1 ? '启用' : '停用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="130">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openCategoryModal(row)">编辑</el-button>
            <el-popconfirm title="确定删除？" @confirm="removeCategory(row.id)">
              <el-button slot="reference" type="text" size="small" class="kb-danger">删除</el-button>
            </el-popconfirm>
          </template>
        </el-table-column>
        <div slot="empty">暂无分类</div>
      </el-table>
    </el-dialog>

    <!-- 分类编辑弹窗 -->
    <el-dialog :visible.sync="categoryModalOpen" :title="editingCategory ? '编辑分类' : '新增分类'" width="480px">
      <el-form ref="categoryFormRef" :model="categoryForm" :rules="categoryRules" label-position="top">
        <el-form-item label="名称" prop="name">
          <el-input v-model="categoryForm.name" />
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="categoryForm.sortOrder" :min="0" class="full-width" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="categoryForm.status" class="full-width">
            <el-option label="启用" :value="1" />
            <el-option label="停用" :value="0" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="categoryModalOpen = false">取消</el-button>
        <el-button type="primary" @click="submitCategory">确定</el-button>
      </div>
    </el-dialog>

    <!-- 批量导入弹窗 -->
    <el-dialog
      :visible.sync="importModalOpen"
      title="批量导入知识库文章"
      :width="previewData && !importResult ? '920px' : '520px'"
      @closed="closeImportModal"
    >
      <!-- 第一步：上传 -->
      <div v-if="!previewData && !importResult" class="import-step">
        <el-alert type="info" show-icon title="使用说明" :closable="false">
          <ol class="import-tips">
            <li>先下载 Excel 模板，按格式填写数据</li>
            <li>分类名称必须与已建立的分类名称一致</li>
            <li>标题和内容为必填字段</li>
            <li>状态填「启用」或「停用」，留空默认为启用</li>
            <li>若标题或菜单代码与现有文章匹配，可选择覆盖</li>
          </ol>
        </el-alert>
        <el-button icon="el-icon-download" @click="handleDownloadTemplate">下载导入模板</el-button>
        <el-upload
          accept=".xlsx,.xls"
          :show-file-list="false"
          :auto-upload="false"
          :on-change="onImportFileChange"
        >
          <el-button icon="el-icon-upload2" :loading="importing" type="primary">选择 Excel 文件上传</el-button>
        </el-upload>
      </div>

      <!-- 第二步：预览 -->
      <div v-if="previewData && !importResult" class="import-step">
        <el-alert
          type="info"
          show-icon
          :message="`解析完成：${previewData.validCount} 篇有效，${previewData.errorCount} 篇错误`"
          :closable="false"
        />
        <el-table :data="previewData.rows" row-key="rowIndex" size="small" max-height="400">
          <el-table-column prop="rowIndex" label="行号" width="60" />
          <el-table-column label="分类" width="110">
            <template slot-scope="{ row }">{{ row.data.categoryName || '-' }}</template>
          </el-table-column>
          <el-table-column label="标题" min-width="140" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.data.title || '-' }}</template>
          </el-table-column>
          <el-table-column label="菜单代码" width="110">
            <template slot-scope="{ row }">{{ row.data.menuCode || '-' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="260">
            <template slot-scope="{ row }">
              <span v-if="!row.valid" class="import-error">{{ row.error }}</span>
              <span v-else-if="row.matches.length === 0" class="import-new">新增</span>
              <el-select
                v-else
                size="small"
                class="full-width"
                :value="previewDecisions[row.rowIndex] || '__new__'"
                @change="(val) => setDecision(row.rowIndex, val)"
              >
                <el-option label="新增" value="__new__" />
                <el-option
                  v-for="m in row.matches"
                  :key="m.id"
                  :label="`覆盖：${m.title} [${m.groupOrCategoryName}] (ID:${m.id})`"
                  :value="m.id"
                />
              </el-select>
            </template>
          </el-table-column>
        </el-table>
        <div class="import-summary">
          共 {{ validRows.length }} 篇有效：{{ newCount }} 篇新增、{{ overwriteCount }} 篇覆盖、{{ previewData.errorCount }} 篇错误跳过
        </div>
      </div>

      <!-- 第三步：结果 -->
      <el-alert
        v-if="importResult"
        :type="importResult.failed > 0 ? 'warning' : 'success'"
        show-icon
        :message="`导入完成：成功 ${importResult.success} 篇，失败 ${importResult.failed} 篇`"
        :closable="false"
      >
        <ul v-if="importResult.errors.length > 0" class="import-errors">
          <li v-for="(e, i) in importResult.errors" :key="i">{{ e }}</li>
        </ul>
      </el-alert>

      <div slot="footer">
        <template v-if="previewData && !importResult">
          <el-button @click="resetImport">重新上传</el-button>
          <el-button
            type="primary"
            :loading="executing"
            :disabled="previewData.validCount === 0"
            @click="handleImportExecute"
          >
            确认导入 ({{ previewData.validCount }} 篇)
          </el-button>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  listCategories, createCategory, updateCategory, deleteCategory,
  listArticles, createArticle, updateArticle, deleteArticle,
  downloadArticleTemplate, batchDeleteArticles, exportArticles,
  previewImportArticles, executeImportArticles
} from '@/api/customerService/knowledge'

function downloadBlob (blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

function pad (n) {
  return n < 10 ? '0' + n : '' + n
}

export default {
  name: 'KnowledgeList',
  data () {
    return {
      articles: [],
      total: 0,
      page: 1,
      categories: [],
      filterCategory: undefined,
      keyword: '',
      loading: false,

      modalOpen: false,
      editingArticle: null,
      articleForm: {
        categoryId: undefined,
        menuCode: '',
        title: '',
        content: '',
        keywords: '',
        status: 1
      },
      articleRules: {
        categoryId: [{ required: true, message: '请选择分类', trigger: 'change' }],
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        content: [{ required: true, message: '请输入内容', trigger: 'blur' }]
      },

      detailDrawerOpen: false,
      detailArticle: null,

      categoryDrawerOpen: false,
      categoryRows: [],
      categoryLoading: false,
      categoryModalOpen: false,
      editingCategory: null,
      categoryForm: { name: '', sortOrder: 0, status: 1 },
      categoryRules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }]
      },

      selectedRowKeys: [],

      importModalOpen: false,
      importing: false,
      importResult: null,
      exporting: false,
      previewData: null,
      previewDecisions: {},
      executing: false
    }
  },
  computed: {
    validRows () {
      return (this.previewData && this.previewData.rows.filter(r => r.valid)) || []
    },
    overwriteCount () {
      return this.validRows.filter(r => this.previewDecisions[r.rowIndex] != null).length
    },
    newCount () {
      return this.validRows.length - this.overwriteCount
    }
  },
  created () {
    this.loadCategories()
    this.loadArticles()
  },
  methods: {
    // ===================== 数据加载 =====================
    async loadArticles () {
      this.loading = true
      try {
        const res = await listArticles({
          categoryId: this.filterCategory,
          keyword: this.keyword,
          pageNum: this.page,
          pageSize: 10
        })
        this.articles = (res.result && res.result.list) || []
        const rawTotal = res.result && res.result.total
        this.total = Number(rawTotal) > 0 ? Number(rawTotal) : 0
      } catch (e) {
        this.$message.error('加载文章失败')
      } finally {
        this.loading = false
      }
    },
    async loadCategories () {
      try {
        const res = await listCategories()
        this.categories = res.result || []
      } catch (e) {
        this.$message.error('加载分类失败')
      }
    },
    onFilterCategoryChange () {
      this.page = 1
      this.loadArticles()
    },
    searchArticles () {
      this.page = 1
      this.loadArticles()
    },
    onPageChange (p) {
      this.page = p
      this.selectedRowKeys = []
      this.loadArticles()
    },
    onSelectionChange (rows) {
      this.selectedRowKeys = rows.map(r => r.id)
    },
    // ===================== 文章维护 =====================
    openArticleModal (article) {
      this.editingArticle = article || null
      this.articleForm = {
        categoryId: (article && article.categoryId) || undefined,
        menuCode: (article && article.menuCode) || '',
        title: (article && article.title) || '',
        content: (article && article.content) || '',
        keywords: (article && article.keywords) || '',
        status: (article && article.status) || 1
      }
      this.modalOpen = true
    },
    async submitArticle () {
      const formEl = this.$refs.articleFormRef
      if (!formEl) return
      await formEl.validate()
      if (this.editingArticle) {
        await updateArticle(this.editingArticle.id, { ...this.articleForm })
        this.$message.success('文章已更新')
      } else {
        await createArticle({ ...this.articleForm })
        this.$message.success('文章已新增')
      }
      this.modalOpen = false
      this.loadArticles()
    },
    async removeArticle (id) {
      await deleteArticle(id)
      this.$message.success('已删除')
      this.loadArticles()
    },
    openDetailDrawer (article) {
      this.detailArticle = article
      this.detailDrawerOpen = true
    },
    keywordTags (keywords) {
      if (!keywords) return []
      return keywords.split(',').map(k => k.trim()).filter(Boolean)
    },
    // ===================== 分类维护 =====================
    async loadCategoriesForManage () {
      this.categoryLoading = true
      try {
        const res = await listCategories({ all: true })
        this.categoryRows = res.result || []
      } catch (e) {
        this.$message.error('加载分类失败')
      } finally {
        this.categoryLoading = false
      }
    },
    openCategoryDrawer () {
      this.categoryDrawerOpen = true
      this.loadCategoriesForManage()
    },
    openCategoryModal (cat) {
      this.editingCategory = cat || null
      this.categoryForm = {
        name: (cat && cat.name) || '',
        sortOrder: (cat && cat.sortOrder) || 0,
        status: (cat && cat.status) || 1
      }
      this.categoryModalOpen = true
    },
    async submitCategory () {
      const formEl = this.$refs.categoryFormRef
      if (!formEl) return
      await formEl.validate()
      if (this.editingCategory) {
        await updateCategory(this.editingCategory.id, { ...this.categoryForm })
        this.$message.success('分类已更新')
      } else {
        await createCategory({ ...this.categoryForm })
        this.$message.success('分类已新增')
      }
      this.categoryModalOpen = false
      this.loadCategoriesForManage()
      this.loadCategories()
    },
    async removeCategory (id) {
      await deleteCategory(id)
      this.$message.success('已删除')
      this.loadCategoriesForManage()
      this.loadCategories()
    },
    // ===================== 批量操作 =====================
    async handleDownloadTemplate () {
      try {
        const blob = await downloadArticleTemplate()
        downloadBlob(blob, '知识库文章导入模板.xlsx')
      } catch (e) {
        this.$message.error('下载模板失败')
      }
    },
    openImportModal () {
      this.importResult = null
      this.previewData = null
      this.importModalOpen = true
    },
    closeImportModal () {
      this.previewData = null
      this.importResult = null
    },
    resetImport () {
      this.previewData = null
      this.importResult = null
    },
    onImportFileChange (file) {
      if (file && file.raw) this.handleImportPreview(file.raw)
    },
    async handleImportPreview (file) {
      this.importing = true
      this.importResult = null
      this.previewData = null
      this.previewDecisions = {}
      try {
        const res = await previewImportArticles(file)
        const result = res.result
        this.previewData = result
        const decisions = {}
        result.rows.forEach(row => {
          if (row.valid) decisions[row.rowIndex] = null
        })
        this.previewDecisions = decisions
      } catch (e) {
        this.$message.error((e && e.message) || '预览失败')
      } finally {
        this.importing = false
      }
    },
    setDecision (rowIndex, val) {
      this.$set(this.previewDecisions, rowIndex, val === '__new__' ? null : val)
    },
    async handleImportExecute () {
      if (!this.previewData) return
      const items = this.previewData.rows
        .filter(row => row.valid)
        .map(row => ({
          data: row.data,
          overwriteId: this.previewDecisions[row.rowIndex] || null
        }))
      if (items.length === 0) {
        this.$message.warning('没有可导入的数据')
        return
      }
      this.executing = true
      try {
        const res = await executeImportArticles(items)
        const result = res.result
        this.importResult = result
        this.previewData = null
        if (result.success > 0) {
          this.$message.success(`成功导入 ${result.success} 篇`)
          await this.loadArticles()
        }
      } catch (e) {
        this.$message.error((e && e.message) || '导入失败')
      } finally {
        this.executing = false
      }
    },
    async handleBatchDelete () {
      if (this.selectedRowKeys.length === 0) return
      try {
        await batchDeleteArticles(this.selectedRowKeys.map(Number))
        this.$message.success(`已删除 ${this.selectedRowKeys.length} 篇`)
        this.selectedRowKeys = []
        await this.loadArticles()
      } catch (e) {
        this.$message.error((e && e.message) || '批量删除失败')
      }
    },
    async handleExport () {
      this.exporting = true
      try {
        const blob = await exportArticles()
        const d = new Date()
        downloadBlob(blob, `知识库文章_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}.xlsx`)
      } catch (e) {
        this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.kb-page {
  display: flex;
  flex-direction: column;
}

.kb-toolbar-card {
  margin-bottom: 16px;
}

.kb-toolbar-card ::v-deep  .el-card__body {
  padding: 14px 16px;
}

.kb-toolbar {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.kb-filter-category {
  width: 160px;
}

.kb-filter-keyword {
  width: 240px;
}

.kb-toolbar-spacer {
  flex: 1;
}

.kb-pagination {
  margin-top: 16px;
  text-align: right;
}

.kb-detail-content {
  white-space: pre-wrap;
}

.kb-category-toolbar {
  margin-bottom: 12px;
}

.kb-danger {
  color: #f56c6c;
}

.full-width {
  width: 100%;
}

.import-step {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.import-tips {
  margin: 4px 0 0;
  padding-left: 20px;
}

.import-summary {
  color: #909399;
  font-size: 13px;
}

.import-error {
  color: #f56c6c;
  font-size: 12px;
}

.import-new {
  color: #67c23a;
}

.import-errors {
  margin: 8px 0 0;
  padding-left: 20px;
}
</style>